/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { ISignUpEmailVerification, ISignUpItem, ISignUpLoading, ISignUpLocalAccount, ISignUpViewProps } from './sign-up';

const SignUpItem: React.FC<ISignUpItem> = ({
    wrapper,
    label,
    errorMessage,
    input
}) => {

    return (
        <Node {...wrapper}>
            {label}
            {errorMessage}
            {input}
        </Node>
    );
};

const SignUpEmailVerification: React.FC<ISignUpEmailVerification> = ({
    email,
    buttonWrapper,
    buttons,
    successMessage,
    errorMessage
}) => {
    return (
        <>
            <SignUpItem {...email} />
            {successMessage && successMessage.map((message: React.ReactNode) => {
                return (
                    <>
                        {message}
                    </>
                );
            })}
            {errorMessage && errorMessage.map((message: React.ReactNode) => {
                return (
                    <>
                        {message}
                    </>
                );
            })}
            <Node {...buttonWrapper}>
                {buttons && buttons.map((button: React.ReactNode) => {
                    return (
                        <>
                            {button}
                        </>
                    );
                })}
            </Node>
        </>
    );
};

const LocalAccount: React.FC<ISignUpLocalAccount> = ({
    localAccount,
    items,
    emailVerification,
    buttons,
    errorMessage,
    disclaimer
}, props) => {

    const [formatPhoneState, setFormatPhoneState] = React.useState(true);
    const [phoneValidationError, setPhoneValidationError] = React.useState('');
    const phoneRegex: RegExp = /^[+][9][6][6][5][0-9]{8}$/gm;
    const phonePattern = "^[+][9][6][6][5][0-9]{8}$";

    const checkPhone = (event: React.KeyboardEvent<HTMLInputElement>) => {
        const value = event.currentTarget.value;

        if (value == "" || value == null) {
            setPhoneValidationError("Please enter your Mobile No.");
            setFormatPhoneState(false);
            return;
        }

        if (value.substring(0, 5) != '+9665') {
            setPhoneValidationError("Mobile No. is not valid, First four digits should be +9665.")
            setFormatPhoneState(false);
            return;

        } else if (value.length < 13 || value.length > 13) {
            setPhoneValidationError("Mobile No. is not valid, Please Enter 13 Digit Mobile No.");
            setFormatPhoneState(false);
            return;

        } else if (!value.match(phoneRegex)) {
            setPhoneValidationError(props?.resources?.invalidPhoneError);
            setFormatPhoneState(false);
            return;
        }

        setFormatPhoneState(true);
    }

    if (typeof window !== 'undefined') {
        const signUpButton: HTMLElement = document.getElementById('continue') as HTMLElement;
        if (signUpButton !== undefined) {
            signUpButton.onclick = (e) => {
                if (!formatPhoneState) {
                    e.stopImmediatePropagation();
                }
            }
        }
    }

    return (
        <Node {...localAccount}>
            {items && items.map((item: ISignUpItem) => {
                if (item.key === 'email' && emailVerification.isRequired) {
                    return (
                        <Node {...emailVerification.verificationControlWrapper}>
                            <SignUpItem {...item} />
                            <SignUpEmailVerification {...emailVerification} />
                        </Node>
                    );
                }
                if (item.key === 'extension_Phone') {
                    return (
                        <div className={'ms-sign-up__account-item ms-sign-up__account-item-phone entry-item attrEntry'}>
                            <label className={'ms-sign-up__account-item-label'} htmlFor='extension_Phone'>
                                {"Phone *(+9665XXXXXXXX)"}
                            </label>
                            {formatPhoneState === false ? <p className="ms-sign-up__item-error-text">{phoneValidationError}</p> : ''}
                            <input
                                type='text'
                                id={item.key}
                                name={item.key}
                                maxLength={13}
                                pattern={phonePattern}
                                placeholder='+9665XXXXXXXX'
                                className='ms-sign-up__account-item-input ms-sign-up__account-item-phone'
                                onKeyUp={(e) => checkPhone(e)}
                                onPaste={(e) => e.preventDefault()}
                            />
                        </div>
                    );
                }
                return (

                    <SignUpItem {...item} />
                );
            })}
            {errorMessage && errorMessage.map((error: React.ReactNode, index: number) => {
                return (
                    <React.Fragment key={index}>
                        {error}
                    </React.Fragment>
                );
            })}
            {buttons && buttons.map((button: React.ReactNode, index: number) => {
                return (
                    <React.Fragment key={index}>
                        {button}
                    </React.Fragment>
                );
            })}
            {disclaimer}
        </Node>
    );
};

const SignUpLoading: React.FC<ISignUpLoading> = ({
    modal,
    modalBody,
    icon,
    message
}) => {
    return (
        <Node {...modal}>
            <Node {...modalBody}>
                {icon}
                {message}
            </Node>
        </Node>
    );
};

const SignUpView: React.FC<ISignUpViewProps> = props => {

    const {
        signUp,
        viewState,
        loading,
        defaultAADConainer,
        aadConainer,
        heading,
        signUpLocalAccount
    } = props;

    return (
        <Module {...signUp}>
            {viewState.isShowLoading && <Node {...defaultAADConainer} />}
            {viewState.isShowLoading && <SignUpLoading {...loading} />}
            <Node {...aadConainer}>
                {heading}
                {LocalAccount(signUpLocalAccount, props)}
            </Node>
        </Module>
    );
};

export default SignUpView;
